/**
 * TrustBox Component
 *
 * This component integrates the Trustpilot review widget into a React application.
 * It allows for the display of Trustpilot reviews in two formats: vertical and horizontal.
 *
 * Props:
 * - `id` (string): The SKU or ID for the product/service being reviewed. This is required.
 * - `showVertical` (boolean): Determines whether to show the vertical widget (default: true).
 *
 * The component uses the `useLocale` hook to determine the user's locale, which is then mapped
 * to the appropriate Trustpilot business unit ID for rendering the widget correctly.
 *
 * It utilizes the `useEffect` hook to load the Trustpilot widget once the component mounts,
 * ensuring the appropriate widget is displayed based on the `showVertical` prop.
 *
 * The widget will not render if the `id` prop is not provided, enhancing user experience
 * by preventing loading errors.
 *
 * This file should be imported and used wherever Trustpilot reviews need to be displayed
 * in the application.
 */

import React, { useEffect, useRef, useState } from 'react';
import useLocale from 'helpers/hooks/useLocale';
import { TrustPilotData } from 'types/next';

declare global {
  interface Window {
    Trustpilot: any;
  }
}

interface TrustBoxProps {
  id?: string;
  showVertical?: boolean;
  home?: boolean;
  trustPilotdata?: TrustPilotData;
}

const TrustBox: React.FC<TrustBoxProps> = ({ id, showVertical = true, home, trustPilotdata }) => {
  const [mounted, setMounted] = useState(false);
  const refVertical = useRef<HTMLDivElement>(null);
  const refHorizontal = useRef<HTMLDivElement>(null);
  const homePage = useRef<HTMLDivElement>(null);
  const { ctLocale: locale } = useLocale();

  // Map locale to business unit IDs
  const businessUnitIds: Record<string, string> = {
    'en-GB': process.env.NEXT_PUBLIC_BUSINESS_UNIT_ID_UK || '', // UK
    'en-US': process.env.NEXT_PUBLIC_BUSINESS_UNIT_ID_COM || '', // US
    'en-EU': process.env.NEXT_PUBLIC_BUSINESS_UNIT_ID_EU || '', // EU
  };

  // Determine the appropriate business unit ID based on the locale, default to UK
  const businessUnitId = businessUnitIds[locale] || businessUnitIds['en-GB'];

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted) {
      // Load the vertical TrustBox if showVertical is true
      if (window?.Trustpilot && refVertical.current && showVertical) {
        window?.Trustpilot?.loadFromElement(refVertical.current, true);
      }

      // Load the horizontal TrustBox if showVertical is false
      if (window?.Trustpilot && refHorizontal.current && !showVertical) {
        window?.Trustpilot?.loadFromElement(refHorizontal.current, true);
      }
      // Load the homePage TrustBox if home is true
      if (homePage.current && home) {
        window?.Trustpilot?.loadFromElement(homePage.current, true);
      }
    }
  }, [mounted, id, showVertical, home]);

  if (!mounted) {
    return null;
  }

  return (
    <>
      {showVertical && !home && (
        <div
          ref={refVertical}
          className="trustpilot-widget"
          data-locale={locale}
          data-template-id="5717796816f630043868e2e8"
          data-businessunit-id={businessUnitId}
          data-style-height="450"
          data-style-width="100%"
          data-theme="light"
          data-sku={id}
          data-review-languages="en"
          data-no-reviews="show"
          data-fullwidth="true"
        >
          <a href="https://www.trustpilot.com/review/www.americangolf.co.uk" target="_blank" rel="noopener noreferrer">
            Trustpilot
          </a>
        </div>
      )}

      {!showVertical && (
        <div
          ref={refHorizontal}
          className="trustpilot-widget"
          data-locale={locale}
          data-template-id="5406e65db0d04a09e042d5fc"
          data-businessunit-id={businessUnitId}
          data-style-height="28px"
          data-style-width="100%"
          data-sku={id}
        >
          <a href="https://uk.trustpilot.com/review/www.americangolf.co.uk" target="_blank" rel="noopener noreferrer">
            Trustpilot
          </a>
        </div>
      )}

      {home && (
        <div
          ref={homePage}
          className="trustpilot-widget"
          data-locale={locale}
          data-template-id="54ad5defc6454f065c28af8b"
          data-businessunit-id={businessUnitId}
          data-style-height="240px"
          data-style-width="100%"
          data-stars={trustPilotdata?.ratingStarsToShow ?? '4,5'}
          data-review-languages="en"
        >
          <a href="https://uk.trustpilot.com/review/www.americangolf.co.uk" target="_blank" rel="noopener noreferrer">
            Trustpilot
          </a>
        </div>
      )}
    </>
  );
};

export default TrustBox;
